// `patient.note[]` — the patient's standing notes. Shared by the ติดตาม page's
// notes card and the pin chip in PatientLayout's sticky header.
//
// Shape (written by BOTH this app and the live Bubble app):
//   { timestamp, details, popup, employee_id, unique_id, modified_date, modified_by }
// plus `code` — the author's employee code, merged in server-side by an addon on
// GET /patient/{id}. That is why no note needs an /api/employees lookup.
//
// ⚠️ THE NOTES ARE NOT SHORT. Two of the three live popup notes sampled on
// 2026-08-12 are multi-line running DEPOSIT LEDGERS maintained over years —
// "24/7/67 คนไข้มียอดฝากกับทางคลินิก 6001 บาท \n29/9/67 ฝากเพิ่ม 2000 = 8001 บาท\n…".
// Anything that renders `details` MUST use whitespace-pre-wrap and must not
// truncate to a fixed width, or it turns a balance sheet into gibberish.

const arr = (p) => (Array.isArray(p?.note) ? p.note : [])
const ts = (n) => Number(n?.timestamp) || 0

// ⚠️ ALWAYS sort. The upstream `edit` action is implemented as |remove: then
// |push:, so an edited note MOVES TO THE END of the stored array — array order
// carries no meaning at all.
export const patientNotes = (p) => arr(p).slice().sort((a, b) => ts(b) - ts(a))

// Notes flagged to surface outside this page. Sorted like the rest — with a median
// of ~1 note per note-bearing patient, a popup-first sort would be theatre.
export const popupNotes = (p) => patientNotes(p).filter((n) => n?.popup === true)

export const noteId = (n) => String(n?.unique_id || '')

/**
 * A fingerprint of the patient's popup notes, used to decide whether the
 * full-screen alert has already been acknowledged.
 *
 * IT INCLUDES `modified_date`, and that is the whole point. Keying on the note
 * IDS alone would mean someone editing "ค้างเงิน 5,000" into "ค้างเงิน 20,000"
 * never interrupts anyone again, because the alert was acknowledged months ago —
 * the exact case the popup flag exists for. Any change to the set, the text or
 * the pin state produces a different string and the alert fires once more.
 *
 * `timestamp` is folded in as well so a note the server has not yet given a
 * unique_id (mergeNote's local placeholder) still fingerprints distinctly.
 */
export const popupFingerprint = (notes) =>
  (Array.isArray(notes) ? notes : [])
    .map((n) => `${noteId(n)}@${Number(n?.modified_date) || Number(n?.timestamp) || 0}`)
    .sort()
    .join('|')

// The author's code comes free from the GET addon; fall back so an unresolvable
// author still reads as *someone* rather than as blank.
export const noteAuthor = (n) => n?.code || (n?.employee_id ? `#${n.employee_id}` : '—')

// Edit + delete are your own only — the same rule as recall notes and ticket
// follow-ups. Enforced for real in functions/api/patients/note.js, which judges
// the STORED entry; this only shapes the UI.
export const canEditNote = (n, me) => {
  const mine = Number(me?.employee_id) || 0
  return !!mine && Number(n?.employee_id) === mine
}

// UNPINNING IS THE EXCEPTION, and deliberately so. A popup note interrupts every
// colleague who opens the patient, so the person best placed to retire a stale
// alarm is the one reading it — not its author, who may have left the clinic.
// Silencing it changes nothing a colleague wrote: the proxy's `unpin` action
// re-sends the STORED details and can only flip `popup`. Pinning ON is a real
// edit of someone's note and stays canEditNote().
export const canUnpinNote = (n, me) => !!(Number(me?.employee_id) || 0) && n?.popup === true

// "แก้ไขแล้ว" only when the edit is meaningfully later than the write — the upstream
// `add` stamps modified_date from the same clock as timestamp, so they land a few
// ms apart on a brand-new note.
const EDIT_GRACE_MS = 60_000
export const noteEdited = (n) => {
  const m = Number(n?.modified_date) || 0
  return m > 0 && m - ts(n) > EDIT_GRACE_MS
}

/**
 * Merge one write into a local note array.
 *
 * ⚠️ NEVER `setPatient(<the write response>)`. `POST /patient/add-on/{id}` answers
 * with the raw `db.edit` output — it carries NONE of the eight addons that
 * `GET /patient/{id}` attaches. Assigning it would drop `note[].code` (every byline
 * degrades to "#395") and `scale_date`, which PatientLayout feeds to <ScaleTag> in
 * the sticky header shared by EVERY patient sub-page. So we merge locally and keep
 * the enriched fields we already have.
 */
export function mergeNote(list, action, { entry, unique_id, details, popup, me } = {}) {
  const cur = Array.isArray(list) ? list : []
  if (action === 'delete') return cur.filter((n) => noteId(n) !== String(unique_id))
  if (action === 'edit') {
    return cur.map((n) => (noteId(n) === String(unique_id)
      ? { ...n, details, popup, modified_date: Date.now(), modified_by: me?.employee_id || 0 }
      : n))
  }
  // unpin — popup is the ONLY field that moves, matching what the proxy sends.
  // `details` is not touched here either: on someone else's note the caller has
  // no business asserting the text, and the stored value is already in `n`.
  if (action === 'unpin') {
    return cur.map((n) => (noteId(n) === String(unique_id)
      ? { ...n, popup: false, modified_date: Date.now(), modified_by: me?.employee_id || 0 }
      : n))
  }
  // Anything unrecognised leaves the list alone. Falling through to `add` would
  // silently APPEND a note for a caller that meant to change one.
  if (action !== 'add') return cur
  // add — the server mints the real uuid; this placeholder only has to survive
  // until the caller refetches or the next write. It is deliberately NOT usable
  // as an edit/delete target: noteId() returns '' and the UI hides those actions.
  return [...cur, {
    ...(entry || {}),
    details,
    popup,
    timestamp: Date.now(),
    modified_date: Date.now(),
    employee_id: me?.employee_id || 0,
    modified_by: me?.employee_id || 0,
    code: me?.code || me?.nickname || me?.name || '',
    unique_id: '',
  }]
}
